import { useTheme } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import Button from '../../yoda-ui/components/Button';
import { TargetListMode } from 'basics/enums/maya.enums';
import ActionButton from 'components/ActionButton';
import ContentCard from 'components/ContentCard';
import DynamicTargetListForm from 'components/DynamicTargetListForm/DynamicTargetListForm';
import GridList from 'components/GridList/GridList';
import Heading from 'components/Heading';
import { LoadingDialog } from 'components/LoadingDialog/LoadingDialog';
import SyncTimeoutIndicator from 'components/SyncTimeoutIndicator';
import UploadTargetListForm from 'components/UploadTargetListForm/UploadTargetListForm';
import useCepFormTargetListGroup from 'groups/CepFormTargetListGroup/CepFormTargetListGroup.hook';
import {
  targetListGridFilterStyle,
  targetListSelectStyle,
  TargetsActionButtonsStyle,
  TargetsListFooterStyle,
} from 'groups/CepFormTargetListGroup/CepFormTargetListGroup.styles';
import { CepFormTargetListGroupType } from 'groups/CepFormTargetListGroup/CepFormTargetListGroup.types';
import TargetListExport from 'groups/CepFormTargetListGroup/TargetListExport/TargetListExport';
import Box from 'yoda-ui/components/Box';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import RadioGroup from 'yoda-ui/components/Form/RadioGroup';
import SelectYoda from 'yoda-ui/components/Form/SelectYoda';
import { SortType } from 'yoda-ui/components/Form/SelectYoda/SelectYoda.types';
import Tooltip from 'yoda-ui/components/Tooltip';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

const CepFormTargetListGroup: FC<CepFormTargetListGroupType> = ({ syncTargetsLoading }) => {
  const {
    cepFormTargetListGroupConfig,
    data,
    handleRemoveTargets,
    loading,
    onPageChange,
    onPageSizeChange,
    open,
    openModalButtonConfig,
    rowCountState,
    setLoading,
    setOpen,
    selectAllTargetsButtonConfig,
    syncTargetsButtonConfig,
    syncSelectedTargetsButtonConfig,
    t,
    targetListConfig,
    targetListMode,
    updateTargetList,
    removeSelectedTargetsModalButtonConfig,
    openRemoveSelectedModal,
    setOpenRemoveSelectedModal,
    handleRemoveSelectedTargets,
    syncStatusTargetListFilterConfig,
    handleChangeStatusTargetsField,
    providerFields,
    isTooltipForRemoveAllTargetsButton,
    syncExpirationDate,
    handleSyncTimeoutComplete,
  } = useCepFormTargetListGroup();

  const flags = useFlags();
  const theme = useTheme();

  return (
    <>
      <Box>
        <ContentCard>
          <Heading level={ 2 }>
            { t('maya_targetlist_select_title') }
          </Heading>
          <Box>
            <RadioGroup { ...cepFormTargetListGroupConfig.selectTargetList } sx={ targetListSelectStyle } />
          </Box>
        </ContentCard>
      </Box>
      <Box marginTop={ YodaSpacing.large }>
        {
          targetListMode === TargetListMode.dynamic
            ? <DynamicTargetListForm
              updateTargetList={ updateTargetList }
              loading={ loading }
              setLoading={ setLoading }
            />
            : <UploadTargetListForm
              updateTargetList={ updateTargetList }
              loading={ loading }
              setLoading={ setLoading }
            />
        }
        <ContentCard sx={ { paddingBottom: YodaSpacing.medium } }>
          <Heading level={ 2 }>
            { t('maya_cep_target_list_grid_title') }
          </Heading>
          <Box sx={ targetListGridFilterStyle }>
            {
              flags.mayaAkma507EnableTargetsFilter
              && <Box width="100%">
                <YodaFormProvider { ...providerFields }>
                  <SelectYoda
                    onChange={ handleChangeStatusTargetsField }
                    { ...syncStatusTargetListFilterConfig }
                    sortType={ SortType.None }
                  />
                </YodaFormProvider>
              </Box>
            }
            {
              flags.mayaAkma507EnableTargetsSelection
              && <Button
                style={ { whiteSpace: 'nowrap' } }
                buttonType={ selectAllTargetsButtonConfig.buttonProps.buttonType }
                onClick={ selectAllTargetsButtonConfig.buttonProps.onClick }
                disabled={ selectAllTargetsButtonConfig.buttonProps.disabled }
              >
                { selectAllTargetsButtonConfig.label }
              </Button>
            }
          </Box>
          <GridList
            getRowId={ (row) => row._id }
            loading={ loading }
            columnConfig={ targetListConfig }
            rows={ data }
            onPageChange={ onPageChange }
            onPageSizeChange={ onPageSizeChange }
            rowCountState={ rowCountState }
            disableColumnMenu={ true }
            hideFooterPagination={ rowCountState === 0 }
          />
          {
            open && (
              <ConfirmationDialog
                dialogTitle={ t('maya_targetlist_modal_remove') }
                onConfirm={
                  () => {
                    handleRemoveTargets();
                  }
                }
                onConfirmLabel={ t('actions_confirm_label') }
                onDismiss={ () => setOpen(false) }
                onDismissLabel={ t('actions_cancel_label') }
              >
              </ConfirmationDialog>
            )
          }
          {
            openRemoveSelectedModal && flags.mayaAkma507EnableTargetsSelection && (
              <ConfirmationDialog
                dialogTitle={ t('maya_targetlist_modal_remove_selected') }
                onConfirm={
                  () => {
                    handleRemoveSelectedTargets();
                  }
                }
                onConfirmLabel={ t('actions_confirm_label') }
                onDismiss={ () => setOpenRemoveSelectedModal(false) }
                onDismissLabel={ t('actions_cancel_label') }
              >
              </ConfirmationDialog>
            )
          }
          {
            syncTargetsLoading && (
              <LoadingDialog text={ t('maya_cep_targets_loading_message') } />
            )
          }
          <Box sx={ TargetsListFooterStyle(theme) }>
            <Box>
              {
                flags.mayaBiogenlinc4691EnableTargetListExport
                  ? <TargetListExport /> : null
              }
            </Box>
            <Box sx={ TargetsActionButtonsStyle(theme) }>
              <Box>
                {
                  flags.mayaAkma507EnableTargetsSelection
                    && <ActionButton { ...removeSelectedTargetsModalButtonConfig } />
                }
                {
                  flags.akma654EnableSelectiveTargetSync
                    ? <ActionButton
                      { ...syncSelectedTargetsButtonConfig }
                    />
                    : null
                }
              </Box>
              <Box>
                {
                  isTooltipForRemoveAllTargetsButton && !openModalButtonConfig.loading ? (
                    <Tooltip title={ `${t('maya_targetlist_tooltip_message_for_button_activation')}` } arrow>
                      <Box>
                        <ActionButton { ...openModalButtonConfig } />
                      </Box>
                    </Tooltip>
                  )
                    : (
                      <ActionButton { ...openModalButtonConfig } />
                    )
                }
                <ActionButton { ...syncTargetsButtonConfig } />
              </Box>
              {
                syncExpirationDate && (
                  <Box>
                    <SyncTimeoutIndicator
                      expirationDate={ syncExpirationDate }
                      onComplete={ handleSyncTimeoutComplete }
                    />
                  </Box>
                )
              }
            </Box>
          </Box>
        </ContentCard>
      </Box>
    </>

  );
};

export default CepFormTargetListGroup;
